import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Alert, Button, H1, P, TextField } from '@decisiv/ui-components';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import UserArrowRight from '@decisiv/iconix/lib/components/UserArrowRight';
import Key from '@decisiv/iconix/lib/components/Key';
import LocalizedPasswordField from '../../LocalizedPasswordField';
import useCheckUsernameSso from './hooks/useCheckUsernameSso';
import { FormsContainer, StyledAlert } from './components/styledComponents';

function ExtraInfo({ html }) {
  return (
    <P
      weight="regular"
      shade={1}
      style={{
        fontSize: '12px',
        textAlign: 'center',
        marginTop: '35px',
        fontWeight: '400',
      }}
    >
      {/* eslint-disable-next-line react/no-danger */}
      <span dangerouslySetInnerHTML={{ __html: html }} />
    </P>
  );
}

ExtraInfo.propTypes = {
  html: PropTypes.string,
};

ExtraInfo.defaultProps = {
  html: '',
};

// Login screen for the OAuth Authorization Code + PKCE flow served at
// /auth/api_gateway. It uses the same two-step flow as the standard login
// (identity -> SSO username-check -> password) via useCheckUsernameSso:
//   - requires_sso -> the browser is sent to the SSO provider. The server
//     (ApiGatewaySessionsController#new) stores the api_gateway URL as
//     session[:return_to], so after SSO the user returns here and
//     auto_generate_code_when_logged_in issues the authorization code.
//   - allow_password_login -> the password field is revealed and submitting
//     performs a native full-page form POST to /auth/api_gateway (carrying the
//     six OAuth params + auth_key/password as form fields), so #create can 302
//     the browser to the client's external redirect_uri with code and state.
function ApiGatewayLogin({
  csrfToken,
  formAction,
  oauthParams,
  translations,
  requestPasswordClick,
}) {
  const formRef = useRef();
  const [identity, setIdentity] = useState('');
  const [allowPasswordLogin, setAllowPasswordLogin] = useState(false);
  const [error, setError] = useState('');

  const handleAllowPasswordLogin = (shouldAllow) => {
    setAllowPasswordLogin(shouldAllow);
    if (shouldAllow) {
      // Focus the password field once React renders it.
      setTimeout(() => {
        const passwordField = document.querySelector('input[name="password"]');
        if (passwordField) passwordField.focus();
      }, 100);
    }
  };

  const { postCheckSso } = useCheckUsernameSso(
    identity,
    handleAllowPasswordLogin,
    setError,
    csrfToken,
  );

  const submitPassword = () => {
    const form = formRef.current;
    if (!form) return;
    // form.submit() bypasses native constraint validation, so check the
    // required fields first (reportValidity surfaces the browser's messages)
    // before doing the full-page POST.
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
      return;
    }
    form.submit();
  };

  const handleLogIn = () => {
    if (allowPasswordLogin) {
      submitPassword();
    } else if (identity) {
      postCheckSso();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLogIn();
    }
  };

  return (
    <Flex justifyContent="center" alignItems="center" flexDirection="column">
      <FormsContainer>
        <H1>{translations.portalLogin}</H1>
        <form
          ref={formRef}
          method="post"
          action={formAction}
          onSubmit={(e) => e.preventDefault()}
          style={{ width: '100%' }}
        >
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          {Object.entries(oauthParams).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value || ''} />
          ))}

          <Flex
            flexDirection="column"
            style={{
              minWidth: '360px',
            }}
          >
            {error && (
              <StyledAlert>
                <Alert intent="danger" title={error} />
              </StyledAlert>
            )}
            <Flex marginBottom={1}>
              <TextField
                icon={UserArrowRight}
                label={translations.usernameOrEmail}
                name="auth_key"
                required
                value={identity}
                onChange={(e) => setIdentity(e.target.value)}
                onKeyPress={handleKeyPress}
              />
            </Flex>
            {allowPasswordLogin && (
              <>
                <LocalizedPasswordField
                  style={{ marginBottom: '10px' }}
                  icon={Key}
                  label={translations.password}
                  showPasswordLabel={translations.showPassword}
                  hidePasswordLabel={translations.hidePassword}
                  name="password"
                  required
                  autoComplete="off"
                  onKeyPress={handleKeyPress}
                />
                <button
                  type="button"
                  onClick={requestPasswordClick}
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    marginLeft: 'auto',
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    font: 'inherit',
                    color: 'inherit',
                  }}
                >
                  {translations.forgotPassword}
                </button>
              </>
            )}
            <Flex marginTop={1}>
              <div className="btn__element--login-btn">
                <Button
                  text={translations.logIn}
                  onClick={handleLogIn}
                  disabled={!allowPasswordLogin && !identity}
                />
              </div>
            </Flex>
          </Flex>
        </form>
      </FormsContainer>
      <ExtraInfo html={translations.legalAgreementsHtml} />
    </Flex>
  );
}

ApiGatewayLogin.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  formAction: PropTypes.string.isRequired,
  oauthParams: PropTypes.objectOf(PropTypes.string),
  translations: PropTypes.objectOf(PropTypes.string),
  requestPasswordClick: PropTypes.func,
};

ApiGatewayLogin.defaultProps = {
  oauthParams: {},
  translations: {},
  requestPasswordClick: () => {},
};

export default ApiGatewayLogin;
